import { supabase } from '../lib/supabase';

const MAX_SIZE_BYTES = 5 * 1024 * 1024; // 5MB — matches the `avatars` bucket's file_size_limit
const VALID_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
const OUTPUT_SIZE = 512;

export function validateAvatarFile(file) {
  if (!file) throw new Error('No file selected.');
  if (!VALID_TYPES.includes(file.type)) {
    throw new Error('Please upload a JPG, PNG, or WebP image.');
  }
  if (file.size > MAX_SIZE_BYTES) {
    throw new Error('Image is too large. Maximum size is 5MB.');
  }
  return true;
}

function loadImage(file) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = URL.createObjectURL(file);
  });
}

/**
 * Center-crops the image to a square and downsizes it to a fixed avatar
 * resolution, returning a WebP File ready for upload.
 */
export async function prepareAvatarFile(file) {
  validateAvatarFile(file);
  const img = await loadImage(file);
  const side = Math.min(img.width, img.height);
  const sx = (img.width - side) / 2;
  const sy = (img.height - side) / 2;

  const canvas = document.createElement('canvas');
  canvas.width = OUTPUT_SIZE;
  canvas.height = OUTPUT_SIZE;
  const ctx = canvas.getContext('2d');
  ctx.drawImage(img, sx, sy, side, side, 0, 0, OUTPUT_SIZE, OUTPUT_SIZE);

  const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/webp', 0.9));
  return new File([blob], 'avatar.webp', { type: 'image/webp' });
}

/**
 * Uploads the given file to the caller's own folder in the `avatars`
 * Supabase Storage bucket and returns its public URL. Storage RLS only
 * allows a user to write under `{their own user id}/...`.
 */
export async function uploadAvatar(userId, file) {
  const preparedFile = await prepareAvatarFile(file);
  const path = `${userId}/avatar-${Date.now()}.webp`;

  const { error: uploadError } = await supabase.storage
    .from('avatars')
    .upload(path, preparedFile, { contentType: 'image/webp', upsert: false });

  if (uploadError) throw uploadError;

  const { data } = supabase.storage.from('avatars').getPublicUrl(path);
  return data.publicUrl;
}

/**
 * Removes the caller's existing avatar object(s) from storage. Safe to call
 * even if the stored avatar_url doesn't point at our bucket (e.g. a legacy
 * external URL) — in that case this is a no-op and only the DB field clears.
 */
export async function removeAvatarFile(userId, avatarUrl) {
  if (!avatarUrl || !avatarUrl.includes('/avatars/')) return;
  const path = avatarUrl.split('/avatars/')[1];
  if (!path || !path.startsWith(`${userId}/`)) return;
  await supabase.storage.from('avatars').remove([path]);
}
